import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { app, auth, db } from './init';
import { createSubmission } from './submissions';
import { testData } from './test-data.js';

const firebaseConfig = {
  apiKey: "AIzaSyDoa6rnk5YBsgNP7redUsvbi72rGMFGtPo",
  authDomain: "cheerzone-5eb66.firebaseapp.com",
  projectId: "cheerzone-5eb66",
  storageBucket: "cheerzone-5eb66.firebasestorage.app",
  messagingSenderId: "248225192430",
  appId: "1:248225192430:web:b43f85c4723946fb7196ff",
  measurementId: "G-CML4BHQ1Q7"
};

export async function importTestData() {
  for (const [id, data] of Object.entries(testData)) {
    await createSubmission(data);
  }
}

export async function fetchLiveSubmissions() {
  let liveApp;
  try {
    liveApp = initializeApp(firebaseConfig, 'liveApp');
  } catch {
    liveApp = initializeApp(firebaseConfig, 'liveApp' + Date.now());
  }

  const liveDb = getFirestore(liveApp);
  const liveAuth = getAuth(liveApp);

  try {
    await signInAnonymously(liveAuth);
  } catch (e) {
    const msg = e.code === 'auth/network-request-failed'
      ? 'No internet connection. Check your network.'
      : `Sign-in to live Firebase failed (${e.code}).`;
    throw new Error(msg);
  }

  let snapshot;
  try {
    snapshot = await getDocs(collection(liveDb, 'submissions'));
  } catch (e) {
    const msg = e.code === 'permission-denied'
      ? 'No read access to live Firestore. Check the security rules.'
      : e.code === 'unavailable'
      ? 'Live Firebase unreachable. Check your internet connection.'
      : `Fetch failed (${e.code ?? e.message}).`;
    throw new Error(msg);
  }

  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function syncToLocalFirestore(liveData) {
  if (!db._settings.host.includes('localhost')) {
    console.error('❌ ERROR: Niet verbonden met de Firestore emulator!');
    return;
  }

  if (!auth.config.emulator) {
    console.error('❌ ERROR: De Firestore emulator is NIET actief!');
    return;
  }

  console.log('✅ Firestore emulator actief. Synchronisatie gestart...');

  for (const item of liveData) {
    const localDocRef = doc(db, 'submissions', item.id);
    const localDocSnapshot = await getDoc(localDocRef);

    await setDoc(localDocRef, item);
    console.log(`${localDocSnapshot.exists() ? '🔄 Bijgewerkt' : '➕ Toegevoegd'}: ${item.id}`);
  }

  console.log('✅ Synchronisatie voltooid!');
}

export async function syncLiveToLocal(onProgress) {
  if (!db._settings.host.includes('localhost')) {
    throw new Error('Not connected to the local Firestore emulator. Is Docker running?');
  }

  // 1. Fetch live data
  onProgress?.({ phase: 'fetch', message: 'Fetching live data…' });
  const liveData = await fetchLiveSubmissions();
  if (!liveData.length) throw new Error('No submissions found in live Firebase.');

  // 2. Delete all local submissions in batches of 500
  onProgress?.({ phase: 'delete', message: 'Deleting local submissions…' });
  const localSnapshot = await getDocs(collection(db, 'submissions'));
  const deleteBatches = [];
  let batch = writeBatch(db);
  let opCount = 0;
  for (const docSnap of localSnapshot.docs) {
    batch.delete(docSnap.ref);
    opCount++;
    if (opCount === 500) {
      deleteBatches.push(batch.commit());
      batch = writeBatch(db);
      opCount = 0;
    }
  }
  if (opCount > 0) deleteBatches.push(batch.commit());
  await Promise.all(deleteBatches);
  const deleted = localSnapshot.size;

  // 3. Write live data to local in batches of 500
  onProgress?.({ phase: 'write', message: `Writing ${liveData.length} submissions…` });
  const writeBatches = [];
  let writeBatchInst = writeBatch(db);
  let writeCount = 0;
  for (const item of liveData) {
    const { id, ...data } = item;
    writeBatchInst.set(doc(db, 'submissions', id), data);
    writeCount++;
    if (writeCount === 500) {
      writeBatches.push(writeBatchInst.commit());
      writeBatchInst = writeBatch(db);
      writeCount = 0;
    }
  }
  if (writeCount > 0) writeBatches.push(writeBatchInst.commit());
  await Promise.all(writeBatches);

  return { fetched: liveData.length, deleted, written: liveData.length };
}
